/* ==========================================================================
   FLIPSIDE MASTER CANVAS — behavior library
   Zero dependencies. Everything binds via data-fmc-* attributes so sections
   stay declarative. Re-initializes on shopify:section:load for the editor.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- reveal on scroll (+ stagger) ---------- */
  var revealIO = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) { e.target.classList.add('is-rev'); revealIO.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

  function initReveal(root) {
    root.querySelectorAll('[data-fmc-stagger]').forEach(function (group) {
      var step = parseFloat(group.getAttribute('data-fmc-stagger')) || 0.08;
      Array.prototype.forEach.call(group.children, function (child, i) {
        if (!child.hasAttribute('data-fmc-reveal')) child.setAttribute('data-fmc-reveal', '');
        child.style.setProperty('--d', (i * step).toFixed(2) + 's');
      });
    });
    root.querySelectorAll('[data-fmc-reveal]').forEach(function (el) {
      if (reduceMotion) { el.classList.add('is-rev'); return; }
      revealIO.observe(el);
    });
  }

  /* ---------- countdown: [data-fmc-countdown data-deadline="ISO"] ---------- */
  function initCountdown(root) {
    root.querySelectorAll('[data-fmc-countdown]').forEach(function (el) {
      if (el._fmcTimer) clearInterval(el._fmcTimer);
      var deadline = new Date(el.getAttribute('data-deadline')).getTime();
      if (isNaN(deadline)) return;
      var cells = {
        days: el.querySelector('[data-cd="days"]'),
        hours: el.querySelector('[data-cd="hours"]'),
        mins: el.querySelector('[data-cd="mins"]'),
        secs: el.querySelector('[data-cd="secs"]')
      };
      function pad(n) { return n < 10 ? '0' + n : '' + n; }
      function tick() {
        var diff = Math.max(0, deadline - Date.now());
        var d = Math.floor(diff / 864e5), h = Math.floor(diff / 36e5) % 24,
            m = Math.floor(diff / 6e4) % 60, s = Math.floor(diff / 1e3) % 60;
        if (cells.days) cells.days.textContent = pad(d);
        if (cells.hours) cells.hours.textContent = pad(h);
        if (cells.mins) cells.mins.textContent = pad(m);
        if (cells.secs) cells.secs.textContent = pad(s);
        if (diff === 0) clearInterval(el._fmcTimer);
      }
      tick();
      el._fmcTimer = setInterval(tick, 1000);
    });
  }

  /* ---------- count-up: [data-fmc-counter data-to="250" data-duration] ---------- */
  function initCounter(root) {
    root.querySelectorAll('[data-fmc-counter]').forEach(function (el) {
      var to = parseFloat(el.getAttribute('data-to')) || 0;
      var dur = parseFloat(el.getAttribute('data-duration')) || 1600;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(el);
          if (reduceMotion) { el.textContent = to.toLocaleString(); return; }
          var start = performance.now();
          (function frame(now) {
            var p = Math.min(1, (now - start) / dur);
            p = 1 - Math.pow(1 - p, 3); /* easeOutCubic */
            el.textContent = Math.round(to * p).toLocaleString();
            if (p < 1) requestAnimationFrame(frame);
          })(start);
        });
      }, { threshold: 0.4 });
      io.observe(el);
    });
  }

  /* ---------- progress bars: [data-fmc-progress data-value data-max] ---------- */
  function initProgress(root) {
    root.querySelectorAll('[data-fmc-progress]').forEach(function (el) {
      var v = parseFloat(el.getAttribute('data-value')) || 0;
      var max = parseFloat(el.getAttribute('data-max')) || 100;
      var pct = Math.min(100, Math.max(0, (v / max) * 100)).toFixed(1) + '%';
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(el);
          el.style.setProperty('--fmc-progress', pct);
        });
      }, { threshold: 0.4 });
      io.observe(el);
    });
  }

  /* ---------- marquee: [data-fmc-marquee] with .fmc-marquee__track ---------- */
  function initMarquee(root) {
    root.querySelectorAll('[data-fmc-marquee]').forEach(function (el) {
      var track = el.querySelector('.fmc-marquee__track');
      if (!track || track._fmcCloned) return;
      track._fmcCloned = true;
      var original = track.innerHTML;
      /* duplicate until track is at least 2x viewport so -50% loop is seamless */
      var guard = 0;
      while (track.scrollWidth < el.clientWidth * 2 && guard < 10) { track.innerHTML += original; guard++; }
    });
  }

  /* ---------- tabs: [data-fmc-tabs] > buttons [data-fmc-tab] + panels [data-fmc-panel] ---------- */
  function initTabs(root) {
    root.querySelectorAll('[data-fmc-tabs]').forEach(function (wrap) {
      var btns = wrap.querySelectorAll('[data-fmc-tab]');
      var panels = wrap.querySelectorAll('[data-fmc-panel]');
      function select(id) {
        btns.forEach(function (b) { b.setAttribute('aria-selected', b.getAttribute('data-fmc-tab') === id ? 'true' : 'false'); });
        panels.forEach(function (p) { p.hidden = p.getAttribute('data-fmc-panel') !== id; });
      }
      btns.forEach(function (b, i) {
        b.addEventListener('click', function () { select(b.getAttribute('data-fmc-tab')); });
        b.addEventListener('keydown', function (ev) {
          if (ev.key !== 'ArrowRight' && ev.key !== 'ArrowLeft') return;
          var next = ev.key === 'ArrowRight' ? btns[(i + 1) % btns.length] : btns[(i - 1 + btns.length) % btns.length];
          next.focus(); next.click();
        });
      });
      if (btns.length) select(btns[0].getAttribute('data-fmc-tab'));
    });
  }

  /* ---------- exclusive accordion: [data-fmc-accordion="exclusive"] of <details> ---------- */
  function initAccordion(root) {
    root.querySelectorAll('[data-fmc-accordion="exclusive"]').forEach(function (wrap) {
      wrap.querySelectorAll('details').forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          wrap.querySelectorAll('details[open]').forEach(function (o) { if (o !== d) o.open = false; });
        });
      });
    });
  }

  /* ---------- horizontal rails ---------- */
  function initRail(root) {
    root.querySelectorAll('[data-fmc-rail-wrap]').forEach(function (wrap) {
      var rail = wrap.querySelector('[data-fmc-rail]');
      if (!rail) return;
      var step = function () { return rail.clientWidth * 0.7; };
      wrap.querySelectorAll('[data-fmc-rail-prev]').forEach(function (b) {
        b.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
      });
      wrap.querySelectorAll('[data-fmc-rail-next]').forEach(function (b) {
        b.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });
      });
    });
  }

  /* ---------- hotspots ---------- */
  function positionMobileHotspot(spot) {
    if (!spot || !window.matchMedia('(max-width: 767px)').matches) return;
    var pop = spot.querySelector('.fmc-hotspot__pop');
    var dot = spot.querySelector('.fmc-hotspot__dot');
    if (!pop || !dot) return;

    spot.classList.remove('fmc-pop--above');
    pop.style.setProperty('--fmc-pop-left', '14px');
    pop.style.setProperty('--fmc-pop-top', '96px');
    pop.style.setProperty('--fmc-pop-caret-left', '50%');

    var dotRect = dot.getBoundingClientRect();
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var safeBottom = 112; /* sticky add-to-cart + breathing room */
    var safeTop = 14;
    var gap = 16;
    var maxW = Math.min(316, vw - 28);
    var popH = Math.min(pop.scrollHeight || 190, Math.min(vh * 0.42, 260));
    var centerX = dotRect.left + dotRect.width / 2;
    var left = Math.max(14, Math.min(vw - maxW - 14, centerX - maxW / 2));
    var belowTop = dotRect.bottom + gap;
    var aboveTop = dotRect.top - popH - gap;
    var useAbove = belowTop + popH > vh - safeBottom && aboveTop >= safeTop;
    var top = useAbove ? aboveTop : Math.min(Math.max(safeTop, belowTop), vh - safeBottom - popH);
    var caret = Math.max(18, Math.min(maxW - 18, centerX - left));

    spot.classList.toggle('fmc-pop--above', useAbove);
    pop.style.setProperty('--fmc-pop-left', left.toFixed(1) + 'px');
    pop.style.setProperty('--fmc-pop-top', top.toFixed(1) + 'px');
    pop.style.setProperty('--fmc-pop-caret-left', caret.toFixed(1) + 'px');
  }

  function initHotspot(root) {
    root.querySelectorAll('.fmc-hotspot__dot').forEach(function (dot) {
      dot.addEventListener('click', function (ev) {
        ev.stopPropagation();
        var spot = dot.closest('.fmc-hotspot');
        var was = spot.classList.contains('is-open');
        document.querySelectorAll('.fmc-hotspot.is-open').forEach(function (s) { s.classList.remove('is-open'); });
        if (!was) {
          spot.classList.add('is-open');
          requestAnimationFrame(function () { positionMobileHotspot(spot); });
        }
        dot.setAttribute('aria-expanded', String(!was));
      });
    });
  }
  document.addEventListener('click', function () {
    document.querySelectorAll('.fmc-hotspot.is-open').forEach(function (s) { s.classList.remove('is-open'); });
  });
  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape') document.querySelectorAll('.fmc-hotspot.is-open').forEach(function (s) { s.classList.remove('is-open'); });
  });
  ['scroll', 'resize', 'orientationchange'].forEach(function (evt) {
    window.addEventListener(evt, function () {
      document.querySelectorAll('.fmc-hotspot.is-open').forEach(positionMobileHotspot);
    }, { passive: true });
  });

  /* ---------- sticky CTA bar: [data-fmc-sticky] shows after its section scrolls past ---------- */
  function initSticky(root) {
    root.querySelectorAll('[data-fmc-sticky]').forEach(function (bar) {
      var sentinelSel = bar.getAttribute('data-fmc-sticky');
      var sentinel = sentinelSel ? document.querySelector(sentinelSel) : bar.closest('.shopify-section');
      if (!sentinel) return;
      var dismissed = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (dismissed) return;
          bar.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0);
        });
      }, { threshold: 0 });
      io.observe(sentinel);
      bar.querySelectorAll('[data-fmc-sticky-dismiss]').forEach(function (b) {
        b.addEventListener('click', function () { dismissed = true; bar.classList.remove('is-on'); });
      });
    });
  }

  /* ---------- demo forms (never submit anywhere) ---------- */
  function initDemoForm(root) {
    root.querySelectorAll('[data-fmc-demo-form]').forEach(function (form) {
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        form.classList.add('is-done');
      });
    });
  }

  /* ---------- generic toggle: [data-fmc-toggle="#sel"] toggles .is-open ---------- */
  function initToggle(root) {
    root.querySelectorAll('[data-fmc-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var t = document.querySelector(btn.getAttribute('data-fmc-toggle'));
        if (!t) return;
        var open = t.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
      });
    });
  }

  /* ---------- toast demo: [data-fmc-toast-trigger="#toastId"] ---------- */
  function initToast(root) {
    root.querySelectorAll('[data-fmc-toast-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var toast = document.querySelector(btn.getAttribute('data-fmc-toast-trigger'));
        if (!toast) return;
        toast.classList.add('is-on');
        clearTimeout(toast._fmcT);
        toast._fmcT = setTimeout(function () { toast.classList.remove('is-on'); }, 3200);
      });
    });
  }

  /* ---------- TOC scrollspy: [data-fmc-toc] containing links to #lab-* ---------- */
  function initToc(root) {
    root.querySelectorAll('[data-fmc-toc]').forEach(function (toc) {
      var links = toc.querySelectorAll('a[href^="#lab-"]');
      var map = {};
      links.forEach(function (a) {
        var target = document.getElementById(a.getAttribute('href').slice(1));
        if (target) map[a.getAttribute('href').slice(1)] = a;
      });
      var spy = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var link = map[e.target.id];
          if (!link) return;
          if (e.isIntersecting) {
            links.forEach(function (a) { a.classList.remove('is-here'); });
            link.classList.add('is-here');
          }
        });
      }, { rootMargin: '-30% 0px -60% 0px' });
      Object.keys(map).forEach(function (id) { spy.observe(document.getElementById(id)); });
    });
  }

  /* ---------- boot ---------- */
  function init(root) {
    root = root || document;
    initReveal(root); initCountdown(root); initCounter(root); initProgress(root);
    initMarquee(root); initTabs(root); initAccordion(root); initRail(root);
    initHotspot(root); initSticky(root); initDemoForm(root); initToggle(root);
    initToast(root); initToc(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  /* theme editor: re-init a section after Shopify re-renders it */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
